import { MatrixServiceInterface } from "./MatrixServiceInterface";
import { MatrixServiceId } from "./MatrixServiceId";
import {
    MatrixServiceRow,
    matrixServiceSelectorAdapter,
} from "./MatrixServiceSelector";

type Matrix = Record<MatrixServiceId, MatrixServiceRow>;

/* cosmetic helper to make writing the actual matrix easier */
const x = 1;

/**
 * Question matrix data. Source of truth is specification in confluence.
 *
 * x means enabled och 0 means hide.
 *
 * Each row correspons with an item that should be enabled/disabled. Rows are
 * processed left to right.
 *
 * Each column corresponds to a specific scenario with will cause items to be
 * enabled/disabled.
 */

// prettier-ignore
//
//               Privatperson anmäler företag ------+  +------ Myndighet anmäler privatperson
//                                                  |  |
//          Privatperson anmäler privatperson ---+  |  |  +--- Myndighet anmäler företag
//                                               |  |  |  |
const matrix: Matrix = { //                      |  |  |  |
    [MatrixServiceId.ANONYM]:                   [x, x, 0, 0],       // Vill du vara anonym?
    [MatrixServiceId.CONTAINER_1]:              [x, x, 0, 0],       // Container 1 Kontaktuppgifter
    [MatrixServiceId.JOBB]:                     [0, 0, x, x],       // Var jobbar du
    [MatrixServiceId.FORNAMN]:                  [0, 0, x, x],       // Ditt förnamn
    [MatrixServiceId.EFTERNAMN]:                [0, 0, x, x],       // Ditt efternamn
    [MatrixServiceId.TELEFONNUMMER]:            [0, 0, x, x],       // Telefonnummer till dig
    [MatrixServiceId.OVRIG_KONTAKT]:            [0, 0, x, x],       // Övriga kontaktuppgifter
    [MatrixServiceId.ANMALA_PERSON]:            [x, 0, x, 0],       // Vem vill du anmäla?
    [MatrixServiceId.PERSON_FORNAMN]:           [x, 0, x, 0],       // Personens förnamn
    [MatrixServiceId.PERSON_EFTERNAMN]:         [x, 0, x, 0],       // Personens efternamn
    [MatrixServiceId.PERSON_PERSON_NR]:         [0, 0, x, 0],       // Personens personnummer
    [MatrixServiceId.PERSON_KONTAKT]:           [0, 0, x, 0],       // Kontaktuppgifter till personen
    [MatrixServiceId.VET_PERSONNUMMER]:         [x, 0, 0, 0],       // Har du personnummer till personen?
    [MatrixServiceId.ANMALA_FORETAG]:           [0, x, 0, x],       // Vilket företag vill du anmäla?
    [MatrixServiceId.FORETAG_NAMN]:             [0, x, 0, x],       // Företagets namn
    [MatrixServiceId.FORETAG_ORG_NUMMER]:       [0, x, 0, x],       // Organisationsnummer
    [MatrixServiceId.FORETAG_FORETRADARE]:      [0, x, 0, x],       // Namn på bolagets företrädare
    [MatrixServiceId.FORETAG_UPPGIFTER]:        [0, x, 0, x],       // Övriga uppgifter
    [MatrixServiceId.CONTAINER_2]:              [x, x, x, x],       // Container 2 Observationer
    [MatrixServiceId.VILKEN_SITUATION]:         [x, x, 0, 0],       // Vilken situation gäller din misstanke? (fråga)
    [MatrixServiceId.SITUATION_PRIVATPERSON]:   [x, 0, 0, 0],       // Vilken situation gäller din misstanke? (Privatperson)
    [MatrixServiceId.ARBETE_ERSATTNING]:        [x, 0, 0, 0],       // Personen arbetar samtidigt som ersättning betalas ut från FK
    [MatrixServiceId.ENSAMSTAENDE]:             [x, 0, 0, 0],       // Personen är sammanboende men får ersättning som ensamstående
    [MatrixServiceId.BOR_UTOMLANDS]:            [x, 0, 0, 0],       // Personen får ersättning men har flyttat utomlands
    [MatrixServiceId.FEL_ASSISTANS_ERSATTNING]: [x, 0, 0, 0],       // Personen får assistansersättning felaktigt
    [MatrixServiceId.ANNAT_PRIVAT]:             [x, 0, 0, 0],       // Annat (Privatperson)
    [MatrixServiceId.ANNAT_FORETAG]:            [0, x, 0, 0],       // Annat (Företag)
    [MatrixServiceId.ANNAT_PRIVAT_FRAGOR]:      [0, 0, x, x],       // Annat (Privat) Frågorna (oberoende av situation)
    [MatrixServiceId.TYP_AV_ERSATTNING]:        [0, 0, x, x],       // Typ av ersättning (oberoende av situation)
    [MatrixServiceId.SITUATION_FORETAG]:        [0, x, 0, 0],       // Vilken situation gäller din misstanke? (Företag)
    [MatrixServiceId.TANDVARDSSTOD]:            [0, x, 0, 0],       // Statligt tandvårdsstöd
    [MatrixServiceId.ASSISTANSERSATTNING]:      [0, x, 0, 0],       // Assistansersättning
    [MatrixServiceId.PERSON_MEDVETEN]:          [x, 0, 0, 0],       // Tror du personen är medveten...
    [MatrixServiceId.FORETAG_MEDVETET]:         [0, x, 0, 0],       // Tror du att företaget är medvetet...
    [MatrixServiceId.UNDERRATTELSESKYLDIGHET]:  [0, 0, x, x],       // Lämnas uppgifterna med stöd av lagen om underrättelseskyldighet (fråga)
    [MatrixServiceId.BILAGOR]:                  [x, x, x, x],       // Ladda upp bilagor
    [MatrixServiceId.CONTAINER_3]:              [x, x, x, x],       // Container 3 Kvittens
    [MatrixServiceId.SPARA_SKRIV_UT]:           [0, 0, x, x],       // Spara och skriv ut

//  ~~~~~~~~~~~~~~~~~~~~~~                      ~~~~~~~~~~~~
//  ^                                           ^
//  T                                           ROW
};

/**
 * Question matrix implementation.
 *
 * @param <T> - Row key/identifier datatype
 * @param <ROW> - Row datatype
 * @param <SELECTOR> - Row selector datatype.
 */
export class MatrixService<T extends string | number | symbol, ROW, SELECTOR>
    implements MatrixServiceInterface<T, SELECTOR>
{
    private matrix: Record<T, ROW>;
    private adapter: (row: ROW, selector: SELECTOR) => boolean;

    public constructor(
        matrix: Record<T, ROW>,
        adapter: (row: ROW, selector: SELECTOR) => boolean,
    ) {
        this.matrix = matrix;
        this.adapter = adapter;
    }

    public isEnabled(id: T, selector: SELECTOR): boolean {
        const row = this.matrix[id];
        if (!row || !selector) {
            /* assume visible for unknown ids or missing selector */
            return true;
        }
        return this.adapter(row, selector);
    }
}

export const MatrixServiceImpl = new MatrixService(
    matrix,
    matrixServiceSelectorAdapter,
);
